import { Plus } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Button } from '../primitives/Button'

interface PrimaryAction {
  label: string
  onClick: () => void
  icon?: React.ReactNode
  variant?: 'default' | 'outline' | 'secondary'
}

interface ListPageHeaderProps {
  /**
   * Primary action button (Create button) - single action
   */
  primaryAction?: PrimaryAction
  /**
   * Multiple primary action buttons - array of actions
   * Use this when you need more than one primary button
   */
  primaryActions?: PrimaryAction[]
  /**
   * Search component (will take flex-grow space)
   */
  search?: React.ReactNode
  /**
   * Secondary buttons/filters (will wrap if needed)
   */
  secondaryActions?: React.ReactNode
  /**
   * Custom className
   */
  className?: string
}

/**
 * ListPageHeader - Header component following Figma design
 * Layout: [Primary Button(s)] [Search (flex-grow)] [Secondary Buttons (wrap)]
 *
 * Structure:
 * - Single row with flex-wrap
 * - Primary button(s): first (order: 0)
 * - Search: second with flex-grow (order: 1)
 * - Secondary buttons: last (order: 2)
 * - Gap: 16px vertical, 8px horizontal
 *
 * Usage:
 * - Use `primaryAction` for a single button
 * - Use `primaryActions` for multiple buttons
 */
export function ListPageHeader({
  primaryAction,
  primaryActions,
  search,
  secondaryActions,
  className,
}: ListPageHeaderProps) {
  // Determine which primary actions to render
  const actionsToRender =
    primaryActions || (primaryAction ? [primaryAction] : [])

  return (
    <div
      className={cn('flex flex-row flex-wrap items-center p-0', className)}
      style={{ gap: '16px 8px' }}
    >
      {/* Primary Actions (CTA) - Order 0, flex-none */}
      {actionsToRender.map((action, index) => (
        <Button
          icon={action.icon || <Plus className="h-4 w-4" />}
          iconPosition="left"
          key={`primary-action-${index}`}
          onClick={action.onClick}
          size="md"
          style={{ order: 0 }}
          variant={action.variant || 'default'}
        >
          {action.label}
        </Button>
      ))}

      {/* Search - Order 1, flex-1 to fill space */}
      {search && (
        <div
          className="min-w-[240px] flex-1 lg:min-w-[600px]"
          style={{ order: 1 }}
        >
          {search}
        </div>
      )}

      {/* Secondary Actions - NO wrapper, render directly */}
      {secondaryActions}
    </div>
  )
}
